import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { scrollToRef } from '../../utility/domHelpers';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer/Footer';
import {
  descriptionCardsStaticProps,
  courseCardsStaticProps,
  businessSectionStaticProps,
  creatorMastercourseSectionStaticProps,
  introBannerStaticPropsV1,
  introBannerStaticPropsV2,
  introBannerStaticPropsV3,
  trailerVideoProps,
  coursePickerStaticProps,
  mobileVideoProps,
  desktopVideoProps,
  courseCardsStaticPropsV2,
  referralInfoModalStaticProps,
} from './constants';
import useCourseBundlesData from '../../hooks/useCourseBundlesData';
import BundlesSection from '../../components/BundlesSection';
import { useHistory, useLocation } from 'react-router-dom';
import { headerDesktopOffset, headerMobileOffset } from './constants';
import CourseCards from './components/CourseCards';
import CreatorMastercourseSection from './components/CreatorMastercourseSection/CreatorMastercourseSection';
import useCourseData from '../../hooks/useCourseData';
import { CREATOR_MASTER_COURSE_CODE } from '../../data/coursesData';
import BusinessSection from './components/BusinessSection/BusinessSection';
import IntroBanner from './components/IntroBanner';
import Modal from '../../components/Modal';
import Video from '../../components/Video';
import DescriptionsSection from '../../components/DescriptionsSection';
import { useWindowWidthContext } from '../../contexts/WindowWidthContext/WindowWidthContext';
import CoursePicker from './components/CoursePicker/CoursePicker';
import ImpactBannerCTA from '../../features/ImpactBannerCTA';
import SilentVideoBanner from './components/SlientVideoBanner';

import ReferralInfoModal from './components/ReferralInfoModal';
import { useUserSessionContext } from '../../contexts/UserSessionContext/UserSessionContext';
import useReferralCode from '../../hooks/useReferralCode';

import './HomePageContainer.scss';

const className = 'c-HomePageContainer';
const DISCOUNT_MOBILE_HEIGHT = 66;
const DISCOUNT_DESKTOP_HEIGHT = 45;

const HomePageContainer = ({ isCreatorVariant, isSilentVideoVariant }) => {
  const bundleRef = useRef(null);
  const creatorRef = useRef(null);
  const location = useLocation();

  const history = useHistory();

  const { setWithValidReferralCode } = useUserSessionContext();

  const { isDesktopView } = useWindowWidthContext();
  const {
    referralDiscountPopupShown,
    turnOffReferralDiscountPopup,
    currentReferralCode,
    setCurrentReferralCode,
    setSessionHomePageLogoLink,
  } = useUserSessionContext();

  const {
    referralCodeOwner,
    isReferralCodeValid,
    referralCode,
  } = useReferralCode(location?.search, setSessionHomePageLogoLink);

  const discountOffsetFactor = isReferralCodeValid ? 1 : 0;
  const scrollNavbarOffset = isDesktopView
    ? 80 + discountOffsetFactor * DISCOUNT_DESKTOP_HEIGHT
    : 64 + discountOffsetFactor * DISCOUNT_MOBILE_HEIGHT;
  const discountOffsetClassName = discountOffsetFactor
    ? `${className}__with-discount-offset`
    : '';

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const { courseBundlesData } = useCourseBundlesData();
  const publicCourseBundlesData = courseBundlesData?.filter(
    (bundle) => bundle?.isPublic
  );

  const { courseOfferings: cmcCourseOfferings } = useCourseData({
    courseCode: CREATOR_MASTER_COURSE_CODE,
    withCountdown: true,
  });

  const cmcVacancyCount = useMemo(() => cmcCourseOfferings?.[0]?.vacancyCount, [
    cmcCourseOfferings,
  ]);

  const introBannerProps = isCreatorVariant
    ? introBannerStaticPropsV2
    : introBannerStaticPropsV1;

  const courseCardProps = isSilentVideoVariant
    ? courseCardsStaticPropsV2
    : courseCardsStaticProps;

  const referralInfoModalProps = Object.assign(
    {},
    referralInfoModalStaticProps,
    {
      title: [
        { text: `Thanks to ${currentReferralCode?.owner},`, isNewLine: true },
        { text: ` Here's your 20% discount!` },
      ],
    }
  );

  const showReferralDiscountPopup =
    !referralDiscountPopupShown && currentReferralCode?.code;

  const onBusinessCTAClick = () => {
    window.open('https://nas.academy/course/business', '_blank');
  };

  const onBannerCTAClick = () => {
    scrollToRef(creatorRef, scrollNavbarOffset);
  };

  const getBundlesNegativeOffset = () =>
    isDesktopView ? -headerDesktopOffset : -headerMobileOffset;

  const getNavInPageScrolls = () => ({
    Bundles: () => scrollToRef(bundleRef, getBundlesNegativeOffset()),
  });

  const goToCoursePickerPage = () => {
    window.open('https://nas.academy/best-for-me', '_blank');
  };

  useEffect(() => {
    if (isReferralCodeValid) {
      setCurrentReferralCode({
        owner: referralCodeOwner,
        code: referralCode,
      });
    }
  }, [
    referralCode,
    referralCodeOwner,
    isReferralCodeValid,
    setCurrentReferralCode,
  ]);

  useEffect(() => {
    if (location?.hash === '#bundle' && bundleRef.current) {
      scrollToRef(bundleRef, getBundlesNegativeOffset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, bundleRef?.current?.offsetTop]);

  useEffect(() => {
    setWithValidReferralCode(isReferralCodeValid);
  }, [isReferralCodeValid, setWithValidReferralCode]);

  const descriptionCardsVariant = isCreatorVariant
    ? descriptionCardsStaticProps.cardsB
    : descriptionCardsStaticProps.cards;
  const descriptionCards = descriptionCardsVariant.map((item) => {
    if (item.cta) {
      const ctaProps = {
        ...item.cta,
        onClick: () => setIsVideoModalOpen(true),
      };
      item.ctaProps = ctaProps;
    }

    return item;
  });

  return (
    <div className={className}>
      <Navbar inPageScrolls={getNavInPageScrolls()} showFullLogo />
      <ImpactBannerCTA />
      <div className={discountOffsetClassName}>
        {isSilentVideoVariant ? (
          <SilentVideoBanner
            {...introBannerStaticPropsV3}
            isDesktopView={isDesktopView}
            mobileVideoProps={mobileVideoProps}
            desktopVideoProps={desktopVideoProps}
          />
        ) : (
          <IntroBanner {...introBannerProps} onCTAClick={onBannerCTAClick} />
        )}

        <div ref={creatorRef}>
          <CourseCards {...courseCardProps} isDesktopView={isDesktopView} />
        </div>

        <CoursePicker
          {...coursePickerStaticProps}
          onBtnClick={goToCoursePickerPage}
        />

        <DescriptionsSection
          title={descriptionCardsStaticProps.title}
          cards={descriptionCards}
        />

        <CreatorMastercourseSection
          {...creatorMastercourseSectionStaticProps}
          vacancyCount={cmcVacancyCount}
          onCtaClick={() => history.push('/course/creator')}
          avgRating={9.2}
          reviewCount="9,400"
          alumniCount="5,500"
        />

        <BundlesSection
          bundleRef={bundleRef}
          bundlesData={publicCourseBundlesData}
        />

        <BusinessSection
          {...businessSectionStaticProps}
          onCTAClick={onBusinessCTAClick}
        />
      </div>
      <Footer />
      {isVideoModalOpen && (
        <Modal
          open={true}
          onClose={() => setIsVideoModalOpen(false)}
          customContainerClass={`${className}__vid-modal-container`}
        >
          <Video {...trailerVideoProps} />
        </Modal>
      )}
      <ReferralInfoModal
        {...referralInfoModalProps}
        open={showReferralDiscountPopup}
        onClose={turnOffReferralDiscountPopup}
      />
    </div>
  );
};

HomePageContainer.defaultProps = {
  isCreatorVariant: false,
};

HomePageContainer.propTypes = {
  isCreatorVariant: PropTypes.bool,
};

export default HomePageContainer;
